import { useCallback, useContext, useRef } from 'react';
import type { AssignedSignee, UnSignedSignatureBox } from 'services/entities/SignatureTypes';
import { useAppSelector } from '../grid/reduxStore/Store';
import { selectSections } from '../grid/reduxStore/editorSlice';
import { SelectionContext } from '../GridDndEditor/SelectedBlockInfoProvider';
import { DocumentSettingsContext } from '../SidePanel/DocumentSettings/DocumentDesignSettings/DocumentSettingsContext';
import { getCanvasWidth } from '../shared/gridConfig';
import { useEffectiveOrientation } from 'hooks/useEffectiveOrientation';
import useActiveSection from '../SectionsDrawer/useActiveSection';
import { getCenteredY, getVisibleHeight } from './sectionViewportUtils';
import { getDefaultSignature, getDefaultSignatureDimensions } from '../signatures/constants';
import { useSignatureInsertHandler } from './useSignatureInsertHandler';

export function useClickToAddSignature() {
  const { selectedSectionId, setSelectedBlockInfoByWrapper } = useContext(SelectionContext);
  const { documentSettings } = useContext(DocumentSettingsContext);
  const { activeSection } = useActiveSection();
  const sections = useAppSelector(selectSections);
  const handleSignatureInsert = useSignatureInsertHandler();
  const canvasWidth = getCanvasWidth(useEffectiveOrientation());
  const isAddingRef = useRef(false);

  return useCallback(
    async (signee: AssignedSignee | null = null) => {
      if (isAddingRef.current) return null;
      isAddingRef.current = true;

      try {
        const targetSectionId =
          selectedSectionId ??
          activeSection ??
          sections
            .map((section) => ({ sectionId: section.id, visibleHeight: getVisibleHeight(section.id) }))
            .sort((a, b) => b.visibleHeight - a.visibleHeight)[0]?.sectionId ??
          sections[0]?.id;

        if (!targetSectionId) {
          return null;
        }

        const { margin } = documentSettings;
        const dimensions = getDefaultSignatureDimensions();
        const availableWidth = canvasWidth - margin.left - margin.right;
        const x = margin.left + (availableWidth - dimensions.width) / 2;
        const y = getCenteredY(targetSectionId, dimensions.height);

        const signature: UnSignedSignatureBox = {
          ...getDefaultSignature(),
          assignedSignee: signee,
          properties: { dimensions, position: { x, y } },
        };

        const signatureBoxId = await handleSignatureInsert(targetSectionId, signature);
        if (signatureBoxId) {
          setSelectedBlockInfoByWrapper({ blockId: signatureBoxId, sectionId: targetSectionId });
        }

        return signatureBoxId;
      } finally {
        isAddingRef.current = false;
      }
    },
    [activeSection, canvasWidth, documentSettings, handleSignatureInsert, sections, selectedSectionId, setSelectedBlockInfoByWrapper]
  );
}
